import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { RaptorTheme } from '../../../../styling/theme';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import GridItem from '../../../layout/GridComponents/GridItem';
import DisplayAreaCenteredWrapper from '../../../layout/utilities/displayAreaWrapper';
import FundSummaryContainer from './components/FundSummaryContainer/fundSummaryContainer.component';
import StandardMancoSummaryContainer from './components/MancoSummaryContainer/MancoSummaryContainer.component';
import ExportItem from '../../utilities/RaptorExport/subComponents/ExportItem.component';
import { Grid, Typography } from '@mui/material';
import {
  EMIR_RESPONSE_MODEL_DETAILED,
  getEmirExportDownloadUrl,
  getEmirExportFileName,
  getEmirResponseModel,
  getEmirReturnedPositionDate,
  useEmirBreakdownQuery,
  type EmirBreakdownPayload,
} from '../../../../api/rs-hg/emir';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  root: {
    transition: 'all .3s',
    '&:hover': {
      transform: 'scale(0.99)',
    },
  },
}));

const EMIR: React.FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
}) => {
  const classes = useStyles();
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const emirQuery = useEmirBreakdownQuery(positionDate);

  const [selectedKey, setSelectedKey] = useState('');
  const updateSelectedKey = (key: string) => {
    setSelectedKey(key);
  };

  useEffect(() => {
    setSelectedKey('');
  }, [positionDate]);

  return (
    <QueryBoundary<EmirBreakdownPayload>
      isLoading={emirQuery.isLoading}
      isFetching={emirQuery.isFetching}
      isError={emirQuery.isError}
      error={emirQuery.error}
      data={emirQuery.data?.data}
      loadingMessages={[
        'Loading Manco Level Data',
        'Manco Level Data May Take a Few Moments to Calculate',
        '...',
      ]}
      errorMessage="There was an error retrieving EMIR data."
      noDataMessage="No EMIR data available."
    >
      {(payload) => {
        const isDetailed =
          getEmirResponseModel(payload) === EMIR_RESPONSE_MODEL_DETAILED;
        const returnedPositionDate = getEmirReturnedPositionDate(
          emirQuery.data,
        );
        const fundsCount = emirQuery.data?.funds.length ?? payload.funds.length;

        return (
          <Grid container spacing={2} style={{ padding: 8 }}>
            {isDetailed && positionDate ? (
              <GridItem
                card
                key="export"
                item
                xs={12}
                cardStyle={{ padding: '0.62rem' }}
              >
                <ExportItem
                  title={'EMIR Export (.xlsx)'}
                  downloadUrl={getEmirExportDownloadUrl(
                    positionDate,
                    raptorPlatformId,
                  )}
                  fileName={getEmirExportFileName(positionDate)}
                  openDownloadInNewTab
                />
              </GridItem>
            ) : null}
            <GridItem
              card
              key="manco-overview"
              item
              classes={classes}
              cardStyle={{ height: '18.75rem' }}
              xs={12}
            >
              <StandardMancoSummaryContainer
                payload={payload}
                fundsCount={fundsCount}
                returnedPositionDate={returnedPositionDate}
                selectedKey={selectedKey}
                setSelectedKeyFunction={updateSelectedKey}
              />
            </GridItem>
            <GridItem
              card
              key="fund-overview"
              item
              cardStyle={{ minHeight: '18.75rem' }}
              xs={12}
            >
              {selectedKey !== '' ? (
                <FundSummaryContainer
                  payload={payload}
                  selectedKey={selectedKey}
                  variant={isDetailed ? 'detailed' : 'standard'}
                  exportPositionDate={positionDate}
                />
              ) : (
                <DisplayAreaCenteredWrapper noMinHeight>
                  <Typography variant="h1">
                    {'Please Click on a Chart Above to Show Fund Data.'}
                  </Typography>
                </DisplayAreaCenteredWrapper>
              )}
            </GridItem>
          </Grid>
        );
      }}
    </QueryBoundary>
  );
};

export default EMIR;
